import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiStats, ApiActivity } from '../types';
import ApiService from '../services/api';
import FluentIcon from '../components/FluentIcon';

interface DashboardProps {}

const Dashboard: React.FC<DashboardProps> = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<ApiStats | null>(null);
  const [activities, setActivities] = useState<ApiActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, activitiesData] = await Promise.all([
          ApiService.getStats(),
          ApiService.getActivities(5)
        ]);
        setStats(statsData);
        setActivities(activitiesData);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // Poll for updates every 30 seconds
    const intervalId = setInterval(fetchData, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '300px',
        flexDirection: 'column'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid rgba(102, 126, 234, 0.2)',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }} />
        <div style={{
          color: '#64748b',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Loading dashboard data...
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="notification notification-warning">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <FluentIcon name="warning" size={20} style={{ marginRight: '8px' }} />
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>Connection Error</h3>
        </div>
        <p style={{ margin: 0, fontSize: '14px' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <div style={{ 
        marginBottom: '40px',
        padding: '32px',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        borderRadius: '20px',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '200px',
          height: '200px',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: '32px',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginBottom: '12px',
            lineHeight: '1.2'
          }}>
            Welcome to WHOIS API Dashboard
          </h1>
          <p style={{ 
            color: '#64748b', 
            fontSize: '18px', 
            margin: 0,
            fontWeight: '400',
            lineHeight: '1.6'
          }}>
            Monitor your domain lookup service performance and manage API resources with real-time insights.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '40px'
      }}>
        {/* Total Requests */}
        <div className="card" style={{
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.1) 100%)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          padding: '28px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(34, 197, 94, 0.05) 100%)',
            borderRadius: '50%',
            filter: 'blur(20px)'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ 
                color: '#15803d', 
                fontSize: '14px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Total Requests
              </div>
              <div style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
              }}>
                <FluentIcon name="chart-line" size={22} />
              </div>
            </div>
            <div style={{ 
              fontSize: '36px', 
              fontWeight: 800, 
              color: '#0f172a', 
              lineHeight: 1,
              marginBottom: '12px'
            }}>
              {stats?.total_requests?.toLocaleString() || '0'}
            </div>
            <div style={{ 
              color: '#22c55e', 
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500'
            }}>
              <span style={{ marginRight: '6px', fontSize: '16px' }}>↗</span>
              +12% from last week
            </div>
          </div>
        </div>

        {/* Active Tokens */}
        <div className="card" style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.1) 100%)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          padding: '28px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
            borderRadius: '50%',
            filter: 'blur(20px)'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ 
                color: '#1d4ed8', 
                fontSize: '14px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Active Tokens
              </div>
              <div style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}>
                <FluentIcon name="key" size={22} />
              </div>
            </div>
            <div style={{ 
              fontSize: '36px', 
              fontWeight: 800, 
              color: '#0f172a', 
              lineHeight: 1,
              marginBottom: '12px'
            }}>
              {stats?.active_tokens || '0'}
            </div>
            <div style={{ 
              color: '#64748b', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              of {stats?.total_tokens || '0'} total tokens
            </div>
          </div>
        </div>

        {/* Supported TLDs */}
        <div className="card" style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05) 0%, rgba(245, 158, 11, 0.1) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          padding: '28px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
            borderRadius: '50%',
            filter: 'blur(20px)'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ 
                color: '#d97706', 
                fontSize: '14px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Supported TLDs
              </div>
              <div style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
              }}>
                <FluentIcon name="globe" size={22} />
              </div>
            </div>
            <div style={{ 
              fontSize: '36px', 
              fontWeight: 800, 
              color: '#0f172a', 
              lineHeight: 1,
              marginBottom: '12px'
            }}>
              {stats?.supported_tlds?.toLocaleString() || '0'}
            </div>
            <div style={{ 
              color: '#64748b', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              domains available
            </div>
          </div>
        </div>

        {/* System Uptime */}
        <div className="card" style={{
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.05) 0%, rgba(168, 85, 247, 0.1) 100%)',
          border: '1px solid rgba(168, 85, 247, 0.2)',
          padding: '28px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)',
            borderRadius: '50%',
            filter: 'blur(20px)'
          }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ 
                color: '#7c3aed', 
                fontSize: '14px', 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                System Uptime
              </div>
              <div style={{
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)',
                borderRadius: '12px',
                color: 'white',
                boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3)'
              }}>
                <FluentIcon name="clock" size={22} />
              </div>
            </div>
            <div style={{ 
              fontSize: '24px', 
              fontWeight: 800, 
              color: '#0f172a', 
              lineHeight: 1,
              marginBottom: '12px'
            }}>
              {stats?.uptime ? formatUptime(stats.uptime) : '0d 0h 0m'}
            </div>
            <div style={{ 
              color: '#22c55e', 
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500'
            }}>
              <div className="status-indicator status-healthy" style={{ marginRight: '8px' }}></div>
              All systems operational
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div style={{ marginBottom: '32px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#212529',
          marginBottom: '16px'
        }}>
          Quick Actions
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}>
          <button 
            onClick={() => navigate('/testing')}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ 
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#e7f5ff',
              borderRadius: '8px',
              color: '#1971c2',
              fontSize: '20px',
              marginBottom: '12px'
            }}><FluentIcon name="beaker" size={20} /></div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#212529', margin: '0 0 4px 0' }}>
              Test API
            </h4>
            <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
              Test WHOIS lookups
            </p>
          </button>

          <button 
            onClick={() => navigate('/tokens')}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ 
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fff9db',
              borderRadius: '8px',
              color: '#e67700',
              fontSize: '20px',
              marginBottom: '12px'
            }}><FluentIcon name="shield-lock" size={20} /></div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#212529', margin: '0 0 4px 0' }}>
              Generate Token
            </h4>
            <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
              Create new API token
            </p>
          </button>

          <button 
            onClick={() => navigate('/health')}
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.2s',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
            }}
          >
            <div style={{ 
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ebfbee',
              borderRadius: '8px',
              color: '#2b8a3e',
              fontSize: '20px',
              marginBottom: '12px'
            }}>❤️</div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#212529', margin: '0 0 4px 0' }}>
              Health Check
            </h4>
            <p style={{ fontSize: '14px', color: '#495057', margin: 0 }}>
              View system status
            </p>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e9ecef'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#212529',
          marginBottom: '16px'
        }}>
          Recent Activity
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {activities.length > 0 ? activities.map((activity, index) => (
            <div key={activity.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
              borderRadius: '6px'
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                backgroundColor: activity.type === 'success' ? '#2b8a3e' : activity.type === 'error' ? '#e03131' : activity.type === 'warning' ? '#f59e0b' : '#1971c2',
                marginRight: '16px'
              }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '14px', color: '#212529', margin: 0, fontWeight: 500 }}>{activity.action}</p>
                <p style={{ fontSize: '12px', color: '#868e96', margin: '4px 0 0 0' }}>{new Date(activity.timestamp).toLocaleString()}</p>
              </div>
            </div>
          )) : (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#868e96',
              fontSize: '14px'
            }}>
              No recent activity
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 